import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart3, TrendingUp, Users, Building2, ShoppingBag, Receipt,
  Loader2, ArrowRight, Trophy, Repeat,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const COLORS = { pro: "#1E3A5F", particulier: "#8FA876", gold: "#C99E30" };

function fmt(c) {
  return ((c || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " €";
}
function fmt2(c) {
  return ((c || 0) / 100).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
function pct(n) {
  return `${(n || 0).toFixed(1)} %`;
}
function monthLabel(m) {
  if (!m) return "—";
  const [y, mm] = m.split("-");
  const months = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];
  return `${months[parseInt(mm) - 1] || mm} ${y.slice(2)}`;
}

const PERIODS = [
  { v: "30d", l: "30 jours" },
  { v: "90d", l: "90 jours" },
  { v: "12m", l: "12 mois" },
  { v: "all", l: "Depuis le début" },
];

export default function ClientAnalyticsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("90d");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/jardins-ole/admin/crm/analytics/segments?period=${period}`);
      setData(r.data || null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period]);

  const pieData = useMemo(() => {
    if (!data) return [];
    return [
      { name: "Pros", value: data.pro?.ca_cents || 0, color: COLORS.pro },
      { name: "Particuliers", value: data.particulier?.ca_cents || 0, color: COLORS.particulier },
    ].filter((d) => d.value > 0);
  }, [data]);

  const monthly = useMemo(() => {
    if (!data) return [];
    return (data.monthly_series || []).map((m) => ({
      label: monthLabel(m.month),
      Pros: m.pro / 100,
      Particuliers: m.particulier / 100,
    }));
  }, [data]);

  return (
    <div className="p-6 space-y-5" data-testid="client-analytics-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" /> Analyse clients · Pros vs Particuliers
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Répartition CA, panier moyen, volume et fréquence d'achat par segment.
          </p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.v}
              onClick={() => setPeriod(p.v)}
              className={`px-3 h-9 rounded-md text-xs font-medium border ${
                period === p.v
                  ? "bg-[color:var(--brand-sky)] text-white border-[color:var(--brand-sky)]"
                  : "bg-white border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]"
              }`}
              data-testid={`period-${p.v}`}
            >
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[hsl(var(--muted-foreground))]">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement...
        </div>
      ) : !data || data.totals.orders === 0 ? (
        <Card className="p-12 text-center">
          <BarChart3 className="w-12 h-12 mx-auto text-[hsl(var(--muted-foreground))]/40 mb-3" />
          <div className="text-[hsl(var(--muted-foreground))]">Aucune commande sur cette période.</div>
        </Card>
      ) : (
        <>
          {/* Top KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Chiffre d'affaires" value={fmt(data.totals.ca_cents)} icon={TrendingUp} testid="kpi-ca-total" />
            <KpiCard label="Commandes totales" value={data.totals.orders} icon={ShoppingBag} testid="kpi-orders-total" />
            <KpiCard label="Part CA pros" value={pct(data.pro_share_ca)} icon={Building2} accent="navy" testid="kpi-pro-share" />
            <KpiCard label="Part commandes pros" value={pct(data.pro_share_orders)} icon={Receipt} testid="kpi-pro-volume" />
          </div>

          {/* Charts row */}
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Pie : CA repartition */}
            <Card className="p-5">
              <div className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">
                Répartition du CA
              </div>
              {pieData.length === 0 ? (
                <div className="h-56 flex items-center justify-center text-sm text-[hsl(var(--muted-foreground))]">—</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={84} paddingAngle={3}>
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmt2(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* Bar chart : monthly trend */}
            <Card className="p-5 lg:col-span-2">
              <div className="text-xs uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-2">
                Évolution mensuelle du CA (€ TTC)
              </div>
              {monthly.length === 0 ? (
                <div className="h-56 flex items-center justify-center text-sm text-[hsl(var(--muted-foreground))]">—</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} €`} />
                    <Tooltip formatter={(v) => `${v.toLocaleString("fr-FR")} €`} />
                    <Legend />
                    <Bar dataKey="Pros" stackId="a" fill={COLORS.pro} />
                    <Bar dataKey="Particuliers" stackId="a" fill={COLORS.particulier} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </div>

          {/* Segment detail cards */}
          <div className="grid md:grid-cols-2 gap-4">
            <SegmentCard
              title="Clients pros"
              icon={Building2}
              color="navy"
              seg={data.pro}
              testidPrefix="seg-pro"
            />
            <SegmentCard
              title="Clients particuliers"
              icon={Users}
              color="green"
              seg={data.particulier}
              testidPrefix="seg-part"
            />
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, accent, testid }) {
  const tone = accent === "navy" ? "text-[color:var(--brand-navy)]" : "";
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
        <span>{label}</span>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <div className={`text-3xl font-bold mt-1 ${tone}`} data-testid={testid}>{value}</div>
    </Card>
  );
}

function SegmentCard({ title, icon: Icon, color, seg, testidPrefix }) {
  const accent = color === "navy" ? "var(--brand-navy)" : "#8FA876";
  return (
    <Card className="p-5 space-y-4" data-testid={testidPrefix}>
      <div className="flex items-center gap-2">
        <span className="inline-flex w-9 h-9 rounded-lg items-center justify-center text-white"
          style={{ background: accent }}>
          <Icon className="w-4 h-4" />
        </span>
        <h3 className="font-bold text-lg">{title}</h3>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">CA</div>
          <div className="text-xl font-bold mt-0.5" style={{ color: accent }}>{fmt(seg.ca_cents)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Commandes</div>
          <div className="text-xl font-bold mt-0.5">{seg.orders}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Clients uniques</div>
          <div className="text-xl font-bold mt-0.5">{seg.customers}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center border-t border-[hsl(var(--border))] pt-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-1">
            <ShoppingBag className="w-3 h-3" /> Panier moyen
          </div>
          <div className="text-lg font-semibold mt-0.5">{fmt2(seg.avg_basket_cents)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-1">
            <Repeat className="w-3 h-3" /> Fréquence
          </div>
          <div className="text-lg font-semibold mt-0.5">{seg.avg_orders_per_client}</div>
          <div className="text-[9px] text-[hsl(var(--muted-foreground))]">cmds/client</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] flex items-center justify-center gap-1">
            <TrendingUp className="w-3 h-3" /> CA/client
          </div>
          <div className="text-lg font-semibold mt-0.5">{fmt(seg.avg_ca_per_client_cents)}</div>
        </div>
      </div>
      {seg.top_clients && seg.top_clients.length > 0 && (
        <div className="border-t border-[hsl(var(--border))] pt-3">
          <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1">
            <Trophy className="w-3 h-3" /> Top 5 clients
          </div>
          <div className="space-y-1.5">
            {seg.top_clients.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="truncate flex-1 mr-2">
                  <span className="font-mono text-[hsl(var(--muted-foreground))]">{i + 1}.</span>{" "}
                  {c.label}
                </span>
                <span className="font-semibold tabular-nums">{fmt(c.ca_cents)}</span>
                <span className="ml-2 text-[10px] text-[hsl(var(--muted-foreground))] tabular-nums">{c.orders} cmd</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}
